import React from 'react';
import { Platform, View } from 'react-native';

import { Pressable, Text, createMqStyles, tokens } from '@fhs/ui';
import { expandUSStateAbbreviationsInAddress } from '@fhs-legacy/frontend/src/utils/restaurant';

import { PredictionsDropdownProps } from './types';

/**
 * The Google Places suggestions panel that hangs below an address field. Callers decide when it
 * is open; it has to sit inside a positioned parent wrapped tightly around the field.
 */
export const PredictionsDropdown: React.VFC<PredictionsDropdownProps> = ({
  predictions,
  isLoading,
  onSelectPrediction,
  getPredictionTestID,
  predictionActionName,
  style,
}) => {
  const styles = usePredictionsDropdownMqStyles();

  if (predictions.length === 0 && isLoading) {
    return null;
  }

  return (
    <View
      style={[styles.dropdown, style]}
      accessibilityRole={Platform.OS === 'web' ? 'list' : undefined}
    >
      <View style={styles.rows}>
        {predictions.length === 0 ? (
          <View style={styles.predictionRow}>
            <Text.Paragraph size="xs" style={styles.predictionSecondary}>
              There are no locations that match your search.
            </Text.Paragraph>
          </View>
        ) : (
          predictions.map((prediction, index) => (
            <Pressable
              key={prediction.placeId}
              testID={getPredictionTestID(index)}
              accessibilityLabel={expandUSStateAbbreviationsInAddress(prediction.description)}
              dd-action-name={predictionActionName}
              onPress={() => onSelectPrediction(prediction)}
              borderRadius={0}
              style={[styles.predictionRow, index > 0 && styles.predictionRowDivided]}
            >
              <Text.Paragraph
                size="sm"
                weight="semibold"
                numberOfLines={1}
                style={styles.predictionMain}
              >
                {prediction.mainText}
              </Text.Paragraph>
              <Text.Paragraph size="xs" numberOfLines={1} style={styles.predictionSecondary}>
                {prediction.secondaryText}
              </Text.Paragraph>
            </Pressable>
          ))
        )}
      </View>
    </View>
  );
};

const usePredictionsDropdownMqStyles = createMqStyles({
  // Floats over whatever follows the field — on desktop that is the tabs and the store list,
  // which must not shift down as the guest types. It must not clip: iOS draws the shadow outside
  // the view's bounds, so clipping here would drop the panel's only edge.
  dropdown: {
    $base: {
      backgroundColor: tokens.colors.$white,
      borderRadius: 4,
      elevation: 8,
      left: 0,
      // Figma all but joins the list to the field — 2pt under it on mobile, 4pt on desktop.
      marginTop: 2,
      position: 'absolute',
      right: 0,
      shadowColor: tokens.colors.$black,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.15,
      shadowRadius: 6,
      top: '100%',
      zIndex: 3,
    },
    $gteDesktop: {
      marginTop: 4,
    },
  },
  // Trims the rows' square corners to the panel's rounded ones.
  rows: {
    $base: {
      borderRadius: 4,
      overflow: 'hidden',
    },
  },
  predictionRow: {
    $base: {
      alignItems: 'flex-start',
      backgroundColor: tokens.colors.$white,
      gap: 3,
      paddingHorizontal: 24,
      paddingVertical: 8,
    },
    $gteDesktop: {
      gap: 5,
    },
  },
  predictionRowDivided: {
    $base: {
      borderTopColor: tokens.colors.$black10,
      borderTopWidth: 1,
    },
  },
  predictionMain: {
    $base: {
      color: tokens.colors.$houseDark,
    },
  },
  predictionSecondary: {
    $base: {
      color: tokens.colors.$blackOpacity75,
    },
  },
});
